/**
 * What each finding IS, and where its element lives on screen.
 *
 * EVERY finding used to link to `/requirements/<element>`. That was right while
 * every check was about a requirement and wrong from the day the first part
 * and case checks landed - and issue #42 adds three about states and parts, so
 * the link has to follow the CHECK rather than assume one kind.
 *
 * Issue #51 adds the rest of what a reader needs: the screen showed the check's
 * internal name - `dangling-in-state (3)` - and nothing about what one means or
 * what to do about it. One entry per check the server can report says both, so
 * a finding is something a person can act on without reading the source.
 *
 * A check with NO entry still renders, under its own name and with no link:
 * the server can gain a check before this file hears of it, and a finding that
 * disappears is worse than one that is plainly labelled.
 */

import type { Finding } from '../schema'

export interface CheckInfo {
  /** What a person calls it. */
  label: string
  /** What one of these means, and what to do about it. */
  help: string
  /** The screen its `element` is an identifier on, or '' for none. */
  plural: string
}

/** Every check the server reports, in the order the kinds are usually read. */
export const CHECKS: Record<string, CheckInfo> = {
  // -- requirements -------------------------------------------------------
  cycle: {
    label: 'Source loop',
    help: 'A requirement’s sources lead back to itself, so it has no real origin. ' +
      'Remove one of the source links in the loop.',
    plural: 'requirements',
  },
  orphan: {
    label: 'No source',
    help: 'A requirement below L0 that names no source, so it is not connected to the ' +
      'flowdown. Give it the requirement it derives from.',
    plural: 'requirements',
  },
  scoping: {
    label: 'Missing segment or subsystem',
    help: 'L2 and below carry a segment; L3 and below also carry a subsystem. Set the one ' +
      'that is empty.',
    plural: 'requirements',
  },
  'no-shall': {
    label: 'Statement does not say shall',
    help: 'A requirement’s statement does not contain the word “shall”, so it reads as a ' +
      'description rather than a requirement. Reword it as “the system shall …”, or confirm ' +
      'it is not meant to be a requirement.',
    plural: 'requirements',
  },
  'duplicate-name': {
    label: 'Duplicate name',
    help: 'Two declarations share one qualified name. BEST lists only one of them, and ' +
      'references resolve to whichever it kept. Rename one in the .sysml.',
    plural: '',
  },
  'undeclared-feature': {
    label: 'Undeclared feature',
    help: 'An element binds a feature nothing it specializes declares - usually a typo, or a ' +
      'feature the profile retired. The binding means nothing until it is renamed or removed.',
    // Could be any kind of element, so it links nowhere rather than to a guess:
    // the message names the element and the line.
    plural: '',
  },
  // -- verification -------------------------------------------------------
  'dangling-case': {
    label: 'Case verifies nothing',
    help: 'A verification case verifies nothing: it names no requirement, or names one the model no ' +
      'longer has - which is what deleting a requirement leaves behind. The case is kept. Point ' +
      'it at the requirement it now verifies, or remove it.',
    plural: 'verification',
  },
  'method-mismatch': {
    label: 'Method differs from nominated',
    help: 'A case verifies by a method its requirement did not nominate. Either is allowed ' +
      'to be right; decide which, and change the other.',
    plural: 'verification',
  },
  // -- parts and interfaces -----------------------------------------------
  'system-type': {
    label: 'No single system',
    help: 'Exactly one part should specialize EchelonSystem; it is the part the system-level ' +
      'figures are reported for. None, or more than one, is reported here.',
    plural: 'parts',
  },
  'untyped-part': {
    label: 'Part outside the profile',
    help: 'A part definition that reaches no EchelonPart, so a profile attribute stated on it ' +
      'redefines nothing. Make it specialize EchelonPart or one of its kinds.',
    plural: 'parts',
  },
  'unused-part': {
    label: 'Unused part definition',
    help: 'A concrete part definition nothing is typed by and nothing specializes, so it is in ' +
      'no decomposition. Add a usage of it inside the part it belongs to, or remove it.',
    plural: 'parts',
  },
  'dangling-in-state': {
    label: 'Value in an unknown state',
    help: 'A part states a value for a state that is not in scope. Correct the state name.',
    plural: 'parts',
  },
  'overlay-conflict': {
    label: 'One figure stated for two states that both hold',
    help: 'A part states a figure for a state and for a state that one includes, so both hold ' +
      'at once. The included state\u2019s figure is the one used. Remove the other if that ' +
      'is not what was meant.',
    plural: 'parts',
  },
  'dangling-connection': {
    label: 'Interface joins nothing',
    help: 'An interface end names something that is not a part or a port in this model. ' +
      'Remove the interface from its part’s pane and connect the ports again.',
    // The element is the INTERFACE, which has no page of its own; the message
    // carries the file and line, and the part pane is where it is removed.
    plural: '',
  },
  // -- behaviour ----------------------------------------------------------
  'dangling-transition': {
    label: 'Transition to an unknown state',
    help: 'A transition names a state that is not in scope. Correct the end, or remove the ' +
      'transition.',
    plural: 'states',
  },
  'dangling-exhibitor': {
    label: 'Exhibited by an unknown part',
    help: 'A state machine names a part that is not in this model. Correct the part.',
    plural: 'states',
  },
  'dangling-include': {
    label: 'Includes an unknown state',
    help: 'A state includes one that is not in scope. Correct the name.',
    plural: 'states',
  },
  'include-conflict': {
    label: 'Includes two states of one machine',
    help: 'Two states of the same machine cannot both hold. Keep one.',
    plural: 'states',
  },
  'dangling-decider': {
    label: 'Decided by an unknown part',
    help: 'A transition is decided by a part that is not in this model. Correct the part.',
    plural: 'states',
  },
  'implicit-initial': {
    label: 'No stated starting state',
    help: 'A machine states no entry, so it starts in its first sub-state by default. That ' +
      'is honoured; mark the state it should start in if the first is not it.',
    plural: 'states',
  },
  'entry-outside-machine': {
    label: 'Starts outside itself',
    help: 'A machine’s starting state is not one of its own. Choose a state inside it.',
    plural: 'states',
  },
  'guarded-entry-only': {
    label: 'Every entry is guarded',
    help: 'Every way into the machine carries a guard, so if none holds it starts nowhere. ' +
      'Legitimate when entry really is conditional; otherwise give it an unguarded start.',
    plural: 'states',
  },
  'unexhibited-state': {
    label: 'Machine nothing exhibits',
    help: 'A state machine no part exhibits, so its states apply to nothing in the design. ' +
      'Say which part exhibits it.',
    plural: 'states',
  },
  'unperformed-function': {
    label: 'Function nothing performs',
    help: 'A top-level function with no performing part, so nothing in the design is ' +
      'responsible for it. Say which part performs it.',
    plural: 'functions',
  },
  // -- stakeholders -------------------------------------------------------
  'unaddressed-concern': {
    label: 'Concern nothing addresses',
    help: 'A stakeholder concern no requirement addresses. Point the requirement that ' +
      'answers it at the concern.',
    plural: 'concerns',
  },
}

/** What to show for a check, known or not. */
export function checkInfo(check: string): CheckInfo {
  return CHECKS[check] ?? { label: check, help: '', plural: '' }
}

/**
 * The address to open for a finding, or null when it names nothing openable.
 *
 * Null for an empty element (`system-type` with no system at all) and for a
 * check this does not know: a link to the wrong kind's screen opens a
 * "not found" that reads as the element being missing, which is worse than no
 * link.
 */
export function findingHref(finding: Finding): string | null {
  const plural = CHECKS[finding.check]?.plural
  if (!plural || !finding.element) return null
  return `/${plural}/${encodeURIComponent(finding.element)}`
}

/**
 * The element id of a check's group on the Findings screen (issue #72).
 *
 * Derived from the check's own name, which is stable, rather than from its
 * label or its position, which are not. The dashboard links to it as a URL
 * hash, so a link and the section it names are built from this one function.
 */
export function findingAnchor(check: string): string {
  return `finding-${check}`
}

/** Errors first: a contradiction outranks a gap, and a gap outranks a note. */
export const SEVERITIES = ['error', 'warning', 'info'] as const

function rank(severity: string): number {
  const index = (SEVERITIES as readonly string[]).indexOf(severity)
  return index === -1 ? SEVERITIES.length : index
}

export interface FindingGroup {
  check: string
  info: CheckInfo
  /** The worst severity in the group, which is what it is sorted and coloured by. */
  severity: string
  findings: Finding[]
}

/**
 * Findings grouped by check, the worst kind first.
 *
 * Grouped because fifty orphans are one problem stated fifty times, and a flat
 * list sorted by nothing put the one source loop - the only error - wherever the
 * server happened to emit it. Within a severity the larger group comes first.
 */
export function groupFindings(findings: readonly Finding[]): FindingGroup[] {
  const byCheck = new Map<string, Finding[]>()
  for (const finding of findings) {
    const group = byCheck.get(finding.check)
    if (group) group.push(finding)
    else byCheck.set(finding.check, [finding])
  }
  return [...byCheck.entries()]
    .map(([check, group]) => ({
      check,
      info: checkInfo(check),
      severity: [...group].sort((a, b) => rank(a.severity) - rank(b.severity))[0].severity,
      findings: group,
    }))
    .sort((a, b) =>
      rank(a.severity) - rank(b.severity) ||
      b.findings.length - a.findings.length ||
      a.info.label.localeCompare(b.info.label))
}

/** How many findings there are at each severity. */
export function severityCounts(findings: readonly Finding[]): Record<string, number> {
  const counts: Record<string, number> = {}
  for (const finding of findings) counts[finding.severity] = (counts[finding.severity] ?? 0) + 1
  return counts
}
